import React, { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { scroller } from 'react-scroll/modules';
import { useToast } from '@common/toast';
import BaseInfo from '@components/join/basicInfo';
import SocialHiddenInfo from '@components/join/socialHiddenInfo';
import KcbSmsVerificationRs from '@domain/rs/verification/KcbSmsVerificationRs';
import KcbIpinVerificationRs from '@domain/rs/verification/KcbIpinVerificationRs';
import { baseURL } from '@utils/common';
import classnames from 'classnames/bind';
import styles from './join.module.scss';

const cx = classnames.bind(styles);

const Join = () => {
  const Toast = useToast();

  /**회원가입 후 back하기 위한 조건 */
  const [verificationResponse, setVerificationResponse] = useState<KcbSmsVerificationRs | KcbIpinVerificationRs | null>(
    null,
  );

  const methods = useForm({
    mode: 'all',
    shouldFocusError: true,
  });

  const {
    setError,
    formState: { errors },
  } = methods;

  const onCheckKeyDown = useCallback((e: React.KeyboardEvent) => {
    const enterKye = ['NumpadEnter', 'Enter'];
    if (enterKye.includes(e.code)) e.preventDefault();
  }, []);

  //아이핀 인증 처리 로직
  const handleJoinVerification = (verificationRs: KcbSmsVerificationRs | KcbIpinVerificationRs) => {
    //휴대폰 인증인 경우 번호 저장
    if ((verificationRs as KcbSmsVerificationRs).mobile)
      // mobileField.onChange((verificationRs as KcbSmsVerificationRs).mobile);
      setVerificationResponse(verificationRs);
  };

  //아이핀 인증 관련 child window 에서 넘어오는 완료 상태값 처리
  const handleVerificationEvent = async (event: MessageEvent) => {
    try {
      // 신뢰할 수 없는 origin 처리
      if (baseURL !== event.origin) return;
      const verificationRs: KcbSmsVerificationRs | KcbIpinVerificationRs = JSON.parse(event.data);
      //ipin 인증 성공 : T000, sms 인증 성공 : B000
      if (!['T000', 'B000'].includes(verificationRs.code)) {
        Toast({ type: 'error', iconType: 'info', content: '본인인증 실패하였습니다.' });
        return;
      }
      handleJoinVerification(verificationRs);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    //아이핀 인증 완료시 이벤트 리스너 등록
    window.addEventListener('message', handleVerificationEvent, false);
    return () => window.removeEventListener('message', handleVerificationEvent);
  }, []);

  useEffect(() => {
    // 회원 가입 각 항목에 대한 앵커링하기 위한 소스 TODO 추후 key 관리에 대해 개선 필요
    const sortKeyList = [
      'name',
      'email',
      'birthDate',
      'genderFlag',
      'userId',
      'password',
      'rePassword',
      'countryCode',
      'mobile',
      'token',
      'agreeTermsOfServiceYn',
      'agreePrivacyCollectionYn',
    ];
    const errorKeys: string[] = errors
      ? Object.keys(errors).sort((a, b) => sortKeyList.indexOf(a) - sortKeyList.indexOf(b))
      : [];

    if (errorKeys.length) {
      const getScrollName = (key: string) => {
        switch (key) {
          case 'genderFlag':
            return 'birthDate';
          case 'token':
            return 'mobile';
          case 'agreeTermsOfServiceYn':
          case 'agreePrivacyCollectionYn':
            return 'agreeArea';
        }

        return key;
      };

      setError(errorKeys[0], { ...errors[errorKeys[0]] }, { shouldFocus: true });
      scroller.scrollTo(getScrollName(errorKeys[0]), {
        offset: -64,
      });
    }
  }, [errors]);

  return (
    <div className={cx('joinArea')}>
      <div className={cx('titleArea')}>
        <p className={cx('title')}>
          환영합니다!
          <br />
          당신의 취업을 진심으로 응원해요
        </p>
      </div>

      <div className={cx('progressArea')} />

      <FormProvider {...methods}>
        <form autoComplete={'off'} onKeyDown={onCheckKeyDown}>
          <BaseInfo verificationResponse={verificationResponse} />
          {/* 소셜 로그인, 아이핀 인증 값에 따라 값 할당이 이루어지는 부분 */}
          <SocialHiddenInfo verificationResponse={verificationResponse} />
        </form>
      </FormProvider>
    </div>
  );
};

export default Join;
